import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { NavigationService } from '../../../services/navigation/navigation.service';
import { CommonService } from '../../../services/common/common.service';
import { ToolsService } from '../../../services/tools/tools.service';
import { Column } from '../../interfaces/column.interface';
import { CitiesDataService, CitiesColumnsService } from '../../services';
import { DialogCityFormComponent } from './city-form/city-form.component';
import { DialogCityDetailsComponent } from './city-details/city-details.component';
import { City, CitiesResult } from '../../interfaces';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-cities',
  templateUrl: './cities.component.html',
  styleUrls: ['./cities.component.scss'],
  providers: [MessageService, ConfirmationService, DialogService],
})
export class CitiesComponent implements OnInit, OnDestroy {

  private cityAddDialog: DynamicDialogRef = new DynamicDialogRef();
  private cityEditDialog: DynamicDialogRef = new DynamicDialogRef();
  private cityDetailsDialog: DynamicDialogRef = new DynamicDialogRef();
  public cityToDelete: City = {};
  public cities: City[] = [];
  public city: City = {};
  public selectedCities: City[] = [];
  public submitted: boolean = false;
  public parameters: { [param: string]: string | string[] } = { offset: '0', limit: '100' };
  public chunkSize: number = 200;
  public columns: Column[] = [];
  public isLoading: boolean = false;
  public isInitialized: boolean = false;
  public locale: string = (localStorage.language) ? localStorage.language : 'en';
  private _selectedColumns: Column[] = [];
  @ViewChild('importer', { static: false })
  public importer?: FileUpload;
  public importChooseLabel: string = 'Import';
  public importLabel: string = 'Import CSV';

  constructor(
    private translate: TranslateService,
    private navigation: NavigationService,
    private common: CommonService,
    private router: Router,
    private dialogService: DialogService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private primengConfig: PrimeNGConfig,
    private titleService: Title,
    private citiesDataService: CitiesDataService,
    private citiesColumnsService: CitiesColumnsService,
  ) {
    this.importChooseLabel = this.translate.instant('IMPORT');
    this.importLabel = this.translate.instant('IMPORTCSV');
  }

  ngOnInit(): void {
    console.log('CitiesComponent', 'ngOnInit');
    this.isInitialized = true;
    this.titleService.setTitle('Cities - Management');
    this.primengConfig.ripple = true;
    this.getCities();
  }

  ngOnDestroy(): void {
    console.log('CitiesComponent', 'ngOnDestroy');
    this.isInitialized = false;
  }

  getCities(): void {
    if (!this.isInitialized) {
      return;
    }
    if (parseInt(this.parameters.offset + '', 10) === 0) {
      this.isLoading = true;
    }
    this.citiesDataService.getCities(this.parameters).subscribe((response: CitiesResult | any) => {
      this.isLoading = false;
      if (response !== null) {
        this.setColumns();
        if (parseInt(this.parameters.offset + '', 10) === 0) {
          this.cities = response.results;
        } else {
          this.cities = this.cities.concat(response.results);
        }
        if (response.inlineCount > this.cities.length) {
          this.parameters.offset = (parseInt(this.parameters.offset + '', 10) + parseInt(this.parameters.limit + '', 10)) + '';
          this.getCities();
        } else {
          this.parameters.offset = '0';
        }
      }
    }, (error: any) => {
      ;
      this.isLoading = false;
      this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('CITIESNOTLOADED') });
    });
  }

  importCities(event: any): void {
    if (typeof event.currentFiles === 'undefined') {
      return;
    }
    if (event.currentFiles.length === 0) {
      return;
    }
    const inputFile = event.currentFiles[0];
    const reader = new FileReader();
    let rows: any[] = [];
    reader.onload = (e: any) => {
      if (typeof this.importer !== 'undefined') {
        this.importer.clear();
      }
      const text = <string>e.target.result;
      rows = ToolsService.csvToArray(this.columns, text, ',');
      if (rows.length > 0) {
        let proceed = 0;
        const j = rows.length;
        for (let i = 0; i < j; i += this.chunkSize) {
          this.isLoading = true;
          const chunk = rows.slice(i, i + this.chunkSize);
          if (typeof chunk[0].id === 'undefined') {
            this.citiesDataService.addCities(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({ severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('CITIESCREATED') });
                if (proceed === j) {
                  this.getCities();
                }
              } else {
                this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('CITIESNOTCREATED') });
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({ severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('CITIESNOTCREATED') + ' ' + msg });
            });
          } else {
            this.citiesDataService.updateCities(chunk).subscribe((response: any) => {
              this.isLoading = false;
              if (typeof response.length !== 'undefined') {
                proceed += response.length;
                this.messageService.add({ severity: 'success', summary: this.translate.instant('NOTIFICATION'), detail: proceed + ' ' + this.translate.instant('CITIESUPDATED') });
                if (proceed === j) {
                  this.getCities();
                }
              } else {
                this.messageService.add({ severity: 'error', summary: this.translate.instant('ERROR'), detail: this.translate.instant('CITIESNOTUPDATED') });
              }
            }, (error: any) => {
              this.isLoading = false;
              console.warn('import error', error);
              let msg = '';
              if (typeof error.error.length !== 'undefined') {
                msg = error.error;
              } else if (typeof error.error.errors.errors.children !== 'undefined') {
                const children = error.error.errors.errors.children;
                for (const field in children) {
                  if (typeof children[field].errors !== 'undefined') {
                    msg += field + ' ' + children[field].errors.join(', ');
                  }
                }
              }
              this.messageService.add({ severity: 'warn', summary: this.translate.instant('ERROR'), detail: this.translate.instant('CITIESNOTCREATED') + ' ' + msg });
            });
          }
        }
      }
    };
    reader.readAsText(inputFile);
  }

  setColumns() {
    if (this.columns.length > 0) {
      return;
    }
    this.columns = this.citiesColumnsService.getColumns();
    for (let i = 0; i < this.columns.length; i++) {
      if (this.columns[i].show) {
        this._selectedColumns.push(this.columns[i]);
      }
    }
  }

  @Input()
  get selectedColumns(): any[] {
    return this._selectedColumns;
  }

  set selectedColumns(val: any[]) {
    // restore original order
    this._selectedColumns = this.columns.filter((col: any) => val.includes(col));
    this.updateColumns();
  }

  updateColumns() {
    for (let i = 0; i < this.columns.length; i++) {
      const show = this._selectedColumns.includes(this.columns[i]);
      this.columns[i].show = show;
      this.navigation.setParamValue('cities.' + this.columns[i].field + '.showFiled', show);
    }
  }

  deleteSelectedCities() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected Cities?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.citiesDataService.deleteCities(this.selectedCities).subscribe((response) => {
          if (response === null) {
            this.messageService.clear();
            this.messageService.add({ severity: 'success', summary: 'Successful', detail: 'Cities Deleted', life: 3000 });
            this.getCities();
          } else {
            this.messageService.clear();
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Cities not deleted' });
          }
        }, (error: any) => {
          this.messageService.clear();
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Cities not deleted' });
        });
        this.selectedCities = [];
      }
    });
  }

  exportPdf() {
    ToolsService.exportPdf(this.cities, this.columns, 'Cities');
  }

  exportExcel() {
    ToolsService.exportExcel(this.cities, this.columns, 'Cities');
  }

  exportCsv() {
    ToolsService.exportCsv(this.cities, this.columns, 'Cities');
  }

  refresh() {
    this.citiesDataService.clearCache();
    this.getCities();
  }

  cityAdd(): void {
    if (!this.navigation.getParamValue('cities.addInDialog', true)) {
      this.router.navigate(['/cities/add']);
      return;
    }
    this.cityAddDialog = this.dialogService.open(DialogCityFormComponent, {
      header: '', // this.translate.instant('NEWCITY'),
      width: '80%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'add',
        city: { id: null },
        countries: this.citiesColumnsService.countries,
        users: this.citiesColumnsService.users,
      },
    });

    this.cityAddDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({ severity: 'info', summary: 'Success', detail: 'City added' });
        this.getCities();
      }
    });
  }

  cityEdit(city: City): void {
    if (!this.navigation.getParamValue('cities.editInDialog', true)) {
      this.router.navigate(['/cities/edit/' + city.id]);
      return;
    }
    this.cityEditDialog = this.dialogService.open(DialogCityFormComponent, {
      header: '', // this.translate.instant('EDITCITY') + ' ' + city.name,
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        mode: 'edit',
        city: city,
        countries: this.citiesColumnsService.countries,
        users: this.citiesColumnsService.users,
      },
    });

    this.cityEditDialog.onClose.subscribe((response: any) => {
      if (response && response.refresh) {
        this.messageService.clear();
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'City updated' });
        this.getCities();
      }
    });
  }

  cityDetails(city: City): void {
    if (!this.navigation.getParamValue('cities.detailsInDialog', true)) {
      this.router.navigate(['/cities/details/' + city.id]);
      return;
    }
    this.cityDetailsDialog = this.dialogService.open(DialogCityDetailsComponent, {
      width: '70%',
      contentStyle: { 'height': '800px', overflow: 'auto' },
      baseZIndex: 1000,
      data: {
        city: city,
      }
    });
  }

  cityDelete(city: City): void {
    this.cityToDelete = city;
    this.messageService.clear();
    this.messageService.add({
      key: 'cityDelete',
      sticky: true,
      severity: 'warn',
      summary: this.translate.instant('AREYOUSURETODELETE') + city.name + ' ?',
      detail: this.translate.instant('CONFIRMTOPROCCED')
    });
  }

  onDeleteConfirm(): void {
    this.citiesDataService.deleteCity(this.cityToDelete).subscribe((res: any) => {
      this.messageService.clear();
      this.messageService.add({ severity: 'success', summary: 'Success', detail: 'City deleted' });
      this.getCities();
    }, (error: any) => {
      this.messageService.clear();
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'City not deleted' });
    });
  }

  onDeleteReject(): void {
    this.cityToDelete = {};
    this.messageService.clear('cityDelete');
  }

}
